import { useEffect, useContext } from 'react';
import { Box, Typography, Divider, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useActiveOrder } from '../../../contexts/ActiveOrderContext';
import { applyRoundAndFormat } from '../../../utils';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { UserContext } from '../../../components/shared/useUser';

const ActiveOrder: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currencyCode , getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { activeOrder, containerData, isActiveOrder, shippingDetails } = useActiveOrder();
  const { t } = useTranslation('onboardingWizard');
  const { selectedStore } = useContext(UserContext);

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      getCurrencyCodeConfiguration();
    }
  }, [selectedStore?.storeNumber]); 
 
  if (!isActiveOrder || !activeOrder) {
    return null;
  }
  const handleOrderClick = () => {
    navigate(`/order/${activeOrder.id}`);
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';

    const isoDate = dateString.includes('T') ? dateString : dateString + 'T00:00:00';
    const parsedDate = dayjs(isoDate);
    if (!parsedDate.isValid()) {
      return dateString.split('T')[0] || dateString;
    }
    return parsedDate.format('DD/MM/YYYY');
  };

  const getTotalProducts = () => {
    // Count unique products — a product with both a normal and a promo line is still 1 item.
    const uniqueIds = new Set(activeOrder.items?.map((i) => i.itemId) ?? []);
    return uniqueIds.size;
  };

  const getTotalQuantity = () => activeOrder.items?.reduce((total, item) => total + item.quantity, 0) ?? 0;

  const totalDeliveryCharge = () => parseFloat(shippingDetails?.totalDeliveryCharge || shippingDetails?.containerCharge  || '0');

  const getTotalCost = () => (activeOrder.items?.reduce((t: number, i) => t + ((i.isPromoLine ? (i.promoUnitPrice ?? i.price) : i.price) * i.quantity || 0), 0) || 0) + totalDeliveryCharge();
  const getPalletsCount = () => {
    return containerData?.totalPalletsNeeded || 0;
  };

  const getTotalPallets = () => {
    return containerData?.maxPalletsPerContainer || 0;
  };

  const getTotalContainers = () => {
    return containerData?.totalContainersUsed || 1;
  };

  const getContainerFillData = () => {
    if (!containerData?.containerFillPercents?.length) return [{ fillPercent: 0, pallets: 0 }];

    const maxPalletsPerContainer = containerData.maxPalletsPerContainer || 20;
    const totalPallets = containerData.totalPalletsNeeded || 0;

    return containerData.containerFillPercents.map((fillPercent, index) => {
      const isLastContainer = index === containerData.containerFillPercents.length - 1;
      const palletsInContainer = isLastContainer
        ? totalPallets - (index * maxPalletsPerContainer)
        : maxPalletsPerContainer;

      return {
        fillPercent, pallets: Math.min(palletsInContainer, maxPalletsPerContainer),
      };
    });
  };

  const getIncoterm = (deliveryMethod: string): string => {
    if (!deliveryMethod) return '-'; // Default fallback
  
    // Check if it contains a dash (most cases)
    if (deliveryMethod.includes(' - ')) {
      const beforeDash = deliveryMethod.split(' - ')[0].trim();
      return beforeDash;
    }
  
    // Handle special cases like "Delivery UK Only"
    if (deliveryMethod.toLowerCase().includes('delivery uk only')) {
      return 'Delivery UK Only';
    }
  
    // For other cases, try to get first 3 characters if they're letters
    const first3 = deliveryMethod.substring(0, 3).toUpperCase();
    if (/^[A-Z]{3}$/.test(first3)) {
      return first3;
    }
  
    // Fallback - return the whole string if it's short, or first word
    if (deliveryMethod.length <= 8) {
      return deliveryMethod;
    }
  
    return deliveryMethod.split(' ')[0];
  };

  const isPalletOnlyMode = (containerData?.maxPalletsPerContainer || 0) === 1;

  const renderPalletFill = () => {
    
    const containerFillData = getContainerFillData();
    const totalPallets = containerFillData.length;

    return (
      <Box sx={{
        p: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1.5,
        backdropFilter: 'blur(10px)',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}>
            {totalPallets > 1 && (
              <Typography sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'center', mb: 1,
              }}>
                {totalPallets} Pallets
              </Typography>
            )}
            <Box sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              maxWidth: 180,
            }}>
              {containerFillData.map((pallet, index) => {
                const fillColor = pallet.fillPercent >= (containerData?.minFill || 0)
                  ? '#4CAF50'
                  : '#FF9800';
                const fillBg = pallet.fillPercent >= (containerData?.minFill || 0)
                  ? '#A8E6A1'
                  : '#FFE082';

                return (
                  <Box key={index}
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                  >
                    {totalPallets > 1 && (
                      <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.625rem', fontWeight: 500,
                      }}>
                        #{index + 1}
                      </Typography>
                    )}

                    <Box
                      sx={{
                        width: totalPallets > 2 ? 36 : 60,
                        height: totalPallets > 2 ? 50 : 70,
                        border: `2px solid ${fillColor}`,
                        borderRadius: 1,
                        position: 'relative',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        overflow: 'hidden',
                        boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      {/* Fill bar from bottom */}
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: `${pallet.fillPercent}%`,
                        backgroundColor: fillBg,
                        transition: 'height 0.3s ease',
                      }} />
                      {/* Pallet base lines */}
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '6px',
                        backgroundColor: '#A0734A',
                        borderTop: '1px solid #7A5530',
                      }} />
                    </Box>
                    <Typography sx={{
                      color: fillColor,
                      fontSize: '0.625rem',
                      fontWeight: 600,
                    }}>
                      {Math.round(pallet.fillPercent)}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minWidth: '80px',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}>
            <Typography sx={{
              fontWeight: 700,
              color: (containerFillData[containerFillData.length - 1]?.fillPercent || 0) >= (containerData?.minFill || 0)
                ? '#4CAF50'
                : '#FF9800',
              fontSize: '1rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
              mb: 0.5,
            }}>
              Fill {Math.round(containerFillData[containerFillData.length - 1]?.fillPercent || 0)}%
            </Typography>
            <Typography sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.4,
            }}>
              Pallets {totalPallets}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderContainerFill = () => {
    const containerFillData = getContainerFillData();
    const totalContainers = getTotalContainers();
    const totalPalletsNeeded = getPalletsCount();
    const maxPalletsPerContainer = getTotalPallets();

    return (
      <Box sx={{
        p: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1.5,
        backdropFilter: 'blur(10px)',
      }}>        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}>
            {totalContainers > 1 && (
              <Typography sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.75rem',
                fontWeight: 600,
                textAlign: 'center', mb: 1,
              }}>
                {totalContainers} Containers
              </Typography>
            )}
            <Box sx={{
              display: 'flex',
              gap: 1,
              maxWidth: totalContainers > 3 ? 140 : 180,
              overflowX: totalContainers > 2 ? 'auto' : 'visible',
              '&::-webkit-scrollbar': {
                height: '3px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
              },
            }}>
              {containerFillData.map((container, index) => (
                <Box key={index}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                >
                  {totalContainers > 1 && (
                    <Typography sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.625rem', fontWeight: 500,
                    }}>
                      #{index + 1}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      width: totalContainers > 1 ? 60 : 140,
                      height: totalContainers > 1 ? 40 : 70,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: 1,
                      position: 'relative',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      overflow: 'hidden',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                      p: 0.5,
                      minWidth: totalContainers > 1 ? 60 : 140,
                    }}
                  >
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: totalContainers > 1 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                      gridTemplateRows: totalContainers > 1 ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
                      gap: totalContainers > 1 ? '1px' : '3px',
                      height: '100%',
                      width: '100%',
                    }}>
                      {Array.from({ length: maxPalletsPerContainer || 20 }, (_, palletIndex) => {
                        const cols = totalContainers > 1 ? 2 : 4;
                        const rows = totalContainers > 1 ? 3 : 5;
                        const maxPallets = totalContainers > 1 ? 6 : 20;

                        if (palletIndex >= maxPallets) return null;

                        const row = Math.floor(palletIndex / cols);
                        const col = palletIndex % cols;
                        const bottomUpIndex = (rows - 1 - row) * cols + col;
                        const palletPosition = bottomUpIndex + 1;
                        const isFilled = palletPosition <= container.pallets;
                        return (
                          <Box
                            key={palletIndex}
                            sx={{
                              backgroundColor: isFilled
                                ? container.fillPercent >= (containerData?.minFill || 0)
                                  ? '#A8E6A1'
                                  : '#FFE082'
                                : 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '1px',
                              transition: 'all 0.3s ease',
                              border: isFilled
                                ? container.fillPercent >= (containerData?.minFill || 0)
                                  ? '1px solid #4CAF50'
                                  : '1px solid #FF9800'
                                : '1px solid rgba(255, 255, 255, 0.1)',
                              boxShadow: isFilled ? '0 0 2px rgba(255, 255, 255, 0.2)' : 'none',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                  {totalContainers > 1 && (<Typography sx={{
                    color: container.fillPercent >= (containerData?.minFill || 0)
                      ? '#4CAF50'
                      : '#FF9800',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                  }}>
                    {Math.round(container.fillPercent)}%
                  </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>          
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minWidth: totalContainers > 3 ? '100px' : '80px',
            maxWidth: totalContainers > 3 ? '120px' : 'none',
            alignItems: 'flex-start',
            flexShrink: 0,
          }}>
            {totalContainers === 1 ? (<>              
              <Typography sx={{
                fontWeight: 700,
                color: (containerFillData[0]?.fillPercent || 0) >= (containerData?.minFill || 0)
                  ? '#4CAF50'
                  : '#FF9800',
                fontSize: '1rem',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                mb: 0.5,
              }}>
              Fill {Math.round(containerFillData[0]?.fillPercent || 0)}%
              </Typography>
              <Typography sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: 1.4,
              }}>
                Pallets {containerFillData[0]?.pallets || 0}
              </Typography>
              <Typography sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: 1.4,
              }}>
                Total {maxPalletsPerContainer}
              </Typography>
            </>) : (
              <>
                <Typography sx={{
                  fontWeight: 700,
                  color: theme.palette.info.light,
                  fontSize: totalContainers > 3 ? '0.875rem' : '1rem',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                  mb: 0.5,
                  lineHeight: 1.2,
                }}>
                  Avg Fill {Math.round(containerFillData.reduce((sum, c) => sum + c.fillPercent, 0) / containerFillData.length)}%
                </Typography>
                <Typography sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: totalContainers > 3 ? '0.75rem' : '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}>
                  Pallets {totalPalletsNeeded}
                </Typography>                
                <Typography sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: totalContainers > 3 ? '0.75rem' : '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}>
                  Total {maxPalletsPerContainer * totalContainers}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  }; return (
    <Box
      sx={{
        p: 4,
        backgroundColor: theme.palette.primary[600],
        cursor: 'pointer',
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[500]}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: theme.palette.primary[700],
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
        },
      }}
      onClick={handleOrderClick}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        mb: 3,
      }}>
        <ShoppingCart sx={{
          color: 'white',
          fontSize: '1.5rem',
        }} />
        <Typography sx={{
          color: 'white',
          fontSize: '1.125rem',
          fontWeight: 600,
          letterSpacing: '0.5px',
        }}>
          Active Order
        </Typography>
      </Box>
      <Box sx={{
        mb: 3,
        p: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1.5,
        backdropFilter: 'blur(10px)',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              Order #:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {activeOrder.id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              Delivery Date:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {formatDate(activeOrder.deliveryDate)}
            </Typography>
          </Box>          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              Products:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {getTotalProducts()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              Quantity:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {getTotalQuantity()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              Type:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {activeOrder.orderType}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              {t('configurationTab.deliveryMethod')}:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {getIncoterm(shippingDetails?.deliveryMethod || '')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              {t('configurationTab.deliveryCharge')}:
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'white', fontWeight: 600 }}>
              {applyRoundAndFormat(totalDeliveryCharge(), currencyCode)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
              Total:
            </Typography>
            <Typography sx={{
              fontSize: '0.875rem',
              color: theme.palette.warning.light,
              fontWeight: 700,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}>
              {applyRoundAndFormat(getTotalCost(), currencyCode)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{
        my: 2,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        '&::before, &::after': {
          borderColor: 'rgba(255, 255, 255, 0.2)',
        },
      }} />
      {isPalletOnlyMode ? renderPalletFill() : renderContainerFill()}
    </Box>
  );
};

export default ActiveOrder;